import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { cloneDeep } from 'lodash';
import { arrayMove } from 'react-sortable-hoc';
import { v4 as uuidv4 } from 'uuid';
import Country from '../../../models/Country';
import { IndicatorKeyEnum } from '../../../models/IndicatorKeyEnum';
import { getNewFilteredIndicators } from '../../../api/indicatorApi';
import { parseFilterToApiCall } from '../../../services/util';
import { translateMultipleText } from '../../../services/stringUtil';
import { processIndicators } from '../../Portfolio/indicatorHelper';
import { addIndicators } from '../../../redux/actions/indicators';
import { setCurrentIndicator } from '../../../redux/actions/currentIndicator';
import { setIndicatorResponse, setLoading } from '../../../redux/actions/dataTable';
import { resetIndicatorDetailFilter } from '../../../redux/actions/indicatorDetailFilters';
import { updateCreatingOrEditing } from '../../../redux/actions/menuTabs';
import { getProjectsByCountry, setIndicatorProjects } from '../../../redux/actions/indicatorProjects';
import {
  resetIndicatorProjectQuery,
  startIndicatorProjectQuery,
  succeedIndicatorProjectQuery,
} from '../../../redux/actions/indicatorProjectQueries';
import {
  PROJECTS_BY_COUNTRY_QUERY_KEY,
  selectProjectsByCountryQuery,
} from '../../../redux/selectors/indicatorProjectQueries';


const getInputKey = state => JSON.stringify({
  common: state.filterIndicators.common,
  tab: state.menuTabs.portfolioSelectedTab,
});

const getCountryFilterKey = common => JSON.stringify(
  (common.countryId || []).map(country => String(country.id)).sort(),
);

const getCountryIds = indicator => (indicator.data?.completeGroupingValue || [])
  .filter(id => id !== null && id !== undefined)
  .map(id => String(id).trim())
  .filter(id => id.length > 0);

const getCountryBy = (regions, countryId: string): Partial<Country> => {
  for (const region of regions) {
    const country = region.countries.find(item => item.id === Number(countryId));
    if (country) {
      return country;
    }
  }
  return { code: 'REG', id: Number(countryId), name: 'regional', regionId: 0 };
};

export const useProjectsByCountryData = () => {
  const dispatch = useDispatch();
  const store = useStore<any>();
  const { t } = useTranslation();
  const currentIndicator = useSelector<any, any>(state => state.currentIndicator);
  const common = useSelector<any, any>(state => state.filterIndicators.common);
  const tab = useSelector<any, any>(state => state.menuTabs.portfolioSelectedTab);
  const projectsQuery = useSelector(selectProjectsByCountryQuery);
  const inputKey = JSON.stringify({ common, tab });

  const [selectedCountry, setSelectedCountry] = useState<Partial<Country>>(() =>
    common.countryId?.length === 1
      ? getCountryBy(store.getState().regions, String(common.countryId[0].id))
      : {},
  );
  const [isCoordinating, setIsCoordinating] = useState(true);
  const [isChartLoading, setIsChartLoading] = useState(false);
  const [hasChartError, setHasChartError] = useState(false);
  const [completedInputKey, setCompletedInputKey] = useState<string | null>(null);
  const [chartInputKey, setChartInputKey] = useState(inputKey);
  const selectedCountryId = useRef<string | null>(sessionStorage.getItem('COUNTRY_INDICATOR_CODE')?.trim() || null);
  const countryFilterKey = useRef(getCountryFilterKey(common));
  const flowVersion = useRef(0);
  const initialized = useRef(false);
  const chartLoading = useRef(false);
  const acceptedChartInputKey = useRef(inputKey);

  const runFlow = useCallback(async (refreshChart: boolean, requestedCountryId?: string) => {
    const version = ++flowVersion.current;
    const state = store.getState();
    const key = getInputKey(state);
    const filterSnapshot = cloneDeep(state.filterIndicators);
    const nextCountryFilterKey = getCountryFilterKey(filterSnapshot.common);
    const countryFromFilter = filterSnapshot.common.countryId?.length === 1
      ? String(filterSnapshot.common.countryId[0].id)
      : undefined;

    if (countryFilterKey.current !== nextCountryFilterKey) {
      countryFilterKey.current = nextCountryFilterKey;
      selectedCountryId.current = null;
      setSelectedCountry(countryFromFilter ? getCountryBy(state.regions, countryFromFilter) : {});
      sessionStorage.removeItem('COUNTRY_INDICATOR_CODE');
      sessionStorage.removeItem('PaginationIndicator');
    }

    const productFilters = parseFilterToApiCall(filterSnapshot.common.projectTypeFilters);
    const projectFilter = { ...filterSnapshot, projectTypeFilters: productFilters.projectProductTypes };
    const isCurrent = () => version === flowVersion.current && key === getInputKey(store.getState());

    setIsCoordinating(true);
    setHasChartError(false);
    setIsChartLoading(refreshChart);
    chartLoading.current = refreshChart;
    dispatch(setLoading(true));
    dispatch(resetIndicatorProjectQuery(PROJECTS_BY_COUNTRY_QUERY_KEY));

    try {
      let indicator = state.currentIndicator;
      if (refreshChart) {
        const response = await getNewFilteredIndicators({}, productFilters, filterSnapshot.common);
        if (!isCurrent()) {
          return;
        }

        const result = processIndicators(response, {
          titleFromTab: translateMultipleText(' ' + state.menuTabs.portfolioSelectedTab.title, t),
          projectTypeFilter: filterSnapshot.common.projectTypeFilters,
        });
        const summaryIndex = result.findIndex(item => item.id === 15);
        const indicators = summaryIndex === -1 ? result : arrayMove(result, summaryIndex, result.length - 1);
        indicator = indicators.find(item =>
          item.indicatorKey === IndicatorKeyEnum.PORTFOLIO_OVERVIEW_INCLUDING_REGIONAL_PROJECTS_BY_COUNTRY,
        );
        if (!indicator) {
          throw new Error('Country indicator missing from response');
        }

        dispatch(setIndicatorResponse(response));
        dispatch(addIndicators(indicators));
        dispatch(setCurrentIndicator(indicator));
      }

      acceptedChartInputKey.current = key;
      setChartInputKey(key);
      chartLoading.current = false;
      setIsChartLoading(false);

      const countryIds = getCountryIds(indicator);
      const preferredCountryId = requestedCountryId || countryFromFilter || selectedCountryId.current;
      const countryId = preferredCountryId && countryIds.includes(preferredCountryId)
        ? preferredCountryId
        : countryIds[0];

      if (refreshChart || countryId !== selectedCountryId.current || requestedCountryId !== undefined) {
        sessionStorage.removeItem('PaginationIndicator');
      }
      selectedCountryId.current = countryId || preferredCountryId || null;

      if (!countryId) {
        sessionStorage.removeItem('COUNTRY_INDICATOR_CODE');
        const requestId = uuidv4();
        dispatch(startIndicatorProjectQuery(PROJECTS_BY_COUNTRY_QUERY_KEY, requestId));
        dispatch(setIndicatorProjects([]));
        dispatch(succeedIndicatorProjectQuery(PROJECTS_BY_COUNTRY_QUERY_KEY, requestId, []));
      } else {
        sessionStorage.setItem('COUNTRY_INDICATOR_CODE', countryId);
        setSelectedCountry(getCountryBy(store.getState().regions, countryId));
        await dispatch(getProjectsByCountry(countryId, projectFilter, isCurrent));
      }

      if (isCurrent() && store.getState().menuTabs.creatingOrEditing) {
        dispatch(updateCreatingOrEditing(false));
      }
    } catch (error) {
      if (isCurrent()) {
        setHasChartError(true);
      }
    } finally {
      if (isCurrent()) {
        setCompletedInputKey(key);
        setIsCoordinating(false);
        setIsChartLoading(false);
        chartLoading.current = false;
        dispatch(setLoading(false));
      }
    }
  }, [dispatch, store, t]);

  useEffect(() => {
    dispatch(resetIndicatorDetailFilter());
  }, [dispatch]);

  useEffect(() => {
    const refreshChart = initialized.current;
    initialized.current = true;
    runFlow(refreshChart);

    return () => {
      ++flowVersion.current;
      dispatch(resetIndicatorProjectQuery(PROJECTS_BY_COUNTRY_QUERY_KEY));
      dispatch(setLoading(false));
    };
  }, [inputKey, runFlow, dispatch]);

  const selectCountry = (value: { completeGroupingValue: string | number }) => {
    if (chartLoading.current || acceptedChartInputKey.current !== getInputKey(store.getState())) {
      return;
    }
    const countryId = String(value.completeGroupingValue).trim();
    if (getCountryIds(store.getState().currentIndicator).includes(countryId)) {
      runFlow(false, countryId);
    }
  };

  return {
    projects: projectsQuery.data,
    currentIndicator,
    selectedCountry,
    isChartLoading: isChartLoading || chartInputKey !== inputKey,
    isProjectsLoading: isCoordinating || completedInputKey !== inputKey ||
      projectsQuery.status === 'idle' || projectsQuery.status === 'loading',
    hasChartError,
    hasProjectsError: projectsQuery.status === 'failed',
    reloadChart: () => runFlow(true),
    reloadProjects: () => runFlow(false),
    selectCountry,
  };
};
